import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { compose, withProps } from 'recompose';
import { connect } from 'react-redux';
import { Page } from 'components/core/Page';
import { H2,H5,Text, Paragraph } from 'components/core/Typography';
import { UnitIcon } from 'components/unit/UnitIcon';
import { IconText } from 'components/core/IconText';
import { Card } from 'components/core/Card';
import { Image } from 'components/core/Image';
import checkImage from 'assets/images/confirmation/checkout-page-check.svg';
import { UnitReservationFormV2 } from 'components/unit/UnitReservationFormV2';
import { getUnitFacilityLookup } from 'store/selectors/unitLookup';
import { createBooking } from 'store/bookings/actions';
import mapDispatchPropsToActions from 'store/helpers/mapDispatchToActions';
import { FacilityDetails } from 'components/facility/FacilityDetailsV2';
import { getFacilityHoursAndDirection } from 'store/facilities/actions';
import {
	getLocalPhoneNumberByZipCode,
	getLocalPhoneNumberByCityState,
} from 'store/pageMeta/actions';
import { get, intersection } from 'lodash';
import { isVipFacility } from 'utils/facility';
import visaImage from 'assets/images/credit-cards/visa.png';
import mastercardImage from 'assets/images/credit-cards/mastercard.png';
import amexImage from 'assets/images/credit-cards/amex.png';
import discoverImage from 'assets/images/credit-cards/discover.png';

import AmenitiesList from 'components/facility/AmenitiesList/AmenitiesList';
import { getScarcityFlag } from 'utils/store/getScarcityFlag';
import plusIcon from 'assets/images/map/Plus.svg';
import minusIcon from 'assets/images/map/Minus.svg';

import './unitPage.scss';
import { formatUnitSize } from 'utils/unitNameTrim';
import formatUnit from 'utils/store/formatUnit';

const icons = {
	Visa: visaImage,
	Mastercard: mastercardImage,
	AMEX: amexImage,
	Discover: discoverImage,
};

export const enhance = compose(
	connect(
		getUnitFacilityLookup,
		{
			createBooking,
			getFacilityHoursAndDirection,
			getLocalPhoneNumberByZipCode,
			getLocalPhoneNumberByCityState,
		},
		mapDispatchPropsToActions
	),
	withProps(({ facility, unit }) => ({
		creditCards: intersection(
			get(facility, 'amenities.acceptedCreditCards', []),
			Object.keys(icons)
		),
		unit: unit ? formatUnit(unit) : unit,
		isScarce: getScarcityFlag([unit]),
	}))
);

export function UnitReservationPage({
	actions,
	unit,
	facility,
	phone,
	creditCards,
	isScarce,
}) {
	if (!unit || !facility) {
		return <Page className="unit-reservation-page" />;
	}

	const { withIcons, withoutIcons } = AmenitiesList({
		amenitiesList: unit.amenitiesText,
	});

	const [isCollapsed, setIsCollapsed] = useState(true);

	const toggleCollapse = () => {
		setIsCollapsed(!isCollapsed);
	};

	return (
		<Page className="unit-reservation-page">
			<div className="page-container">
				<div className="row content-container checkout-facility-details-container">
					<FacilityDetails
						facilityName={facility.name}
						facilityId={facility.id}
						facilityZipCode={facility.location.zip}
						locationText={facility.location.text}
						isVipFacility={isVipFacility(facility)}
						reviewAverage={get(facility, 'reviews.average')}
						reviewCount={get(facility, 'reviews.count')}
						facilityPhoneNumber={phone?.localSparephone}
						unformattedFacilityPhoneNumber={
							phone?.localSparephoneUnformatted
						}
						onGetHoursAndDirections={
							actions.getFacilityHoursAndDirection
						}
						getLocalPhoneNumberByZipCode={
							actions.getLocalPhoneNumberByZipCode
						}
						getLocalPhoneNumberByCityState={
							actions.getLocalPhoneNumberByCityState
						}
						showPhoneNumber={false}
					/>
				</div>
				<div className='checkout-page-banner-container'>
					<span>Reservation is Free!</span>
					{' '}
					<span>No payment until you move in.</span>
				</div>
				<div className="unit-reservation-page__container">
					<div className="unit-reservation-page__left">
						<Card className="unit-reservation-page__unit-card">
							<div
								className='unit-reservation-page__unit-header-info-new'
								role="button"
								tabIndex={0}
								onClick={toggleCollapse}
								onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCollapse(); } }}
								aria-expanded={!isCollapsed}
							>
								{unit.promotion && (
										<Paragraph
											size="small"
											className="unit-reservation-page__promotion"
											weight="ultraBold"
											family="standard"
										>
											{unit.promotion}
										</Paragraph>
								)}
								<div className='unit-reservation-page__unit-details'>
									<div className='unit-icon-size-container'>
										<UnitIcon
											unitSize={`${unit.sizeBucket}-${unit.unitType}`}
											alt="Unit Size"
											lazyLoad={false}
										/>
										<div className='unit-size-text-container'>
											<Text className="unit-size-label-description">
												{formatUnitSize(unit.sizeText)}{' '}
												{unit.unitTypeText}{' '}
												{unit.giftCardOffer
													?.isGiftCardEligible && (
													<GiftCardPill
														giftCardValue={
															giftCardOffer?.giftCardValue
														}
													/>
												)}
											</Text>
											{isScarce && (
												<Text
													className="scarcity-message"
													last
												>
													ONLY {unit.quantity} LEFT!
												</Text>
											)}
										</div>
									</div>
									<Image
										src={
											isCollapsed
												? plusIcon
												: minusIcon
										}
										alt={
											isCollapsed
												? 'Expand'
												: 'Collapse'
										}
										className={
											isCollapsed
												? 'expand'
												: 'collapse'
										}
									/>
								</div>
							</div>
							<div
								className={classnames(
									'unit-reservation-page__selected-unit-card',
									{
										collapsed: isCollapsed,
										expanded: !isCollapsed,
									}
								)}
							>
								<Card className="unit-reservation-page__card amenities-card">
									{[...withIcons, ...withoutIcons].length > 0 && (
										<>
											<p className='amenities-heading'>Unit Features</p>
											<div className="amenities-without-icon">
												{[...withIcons, ...withoutIcons].map(
													(amenity, index, arr) => (
														<React.Fragment key={amenity.key || amenity.name}>
															<span>
																{amenity.name}
															</span>
															{index < arr.length - 1 && (
																<div className="separator"></div>
															)}
														</React.Fragment>
													)
												)}
											</div>
										</>
									)}
								</Card>
								<Card className="unit-reservation-page__card pricing-summary-card">
									<div className="unit-reservation-page__pricing-header">
										<H5
											className="pricing-header-unit-rent"
										>
											Monthly Unit Rent
										</H5>
										<H5
											className="pricing-header-unit-rent"
										>
											${unit.price.text}
										</H5>
									</div>
									{facility?.fees?.totalFees?.value > 0 && (
										<div className='admin-fees-description-container'>
											<div className="unit-reservation-page__admin-fees">
												<div className="unit-reservation-page__pricing-header">
													<H5
														className="admin-fees-header"
													>
														Admin Fees
													</H5>
													<H5
														className="admin-fees-header"
													>
														$
														{
															facility?.fees
																?.totalFees
																?.text
														}
													</H5>
												</div>
												<div className="unit-reservation-page__pricing">
													<Text
														color="black"
														size="smaller"
													>
														The following fees shall
														apply:
													</Text>
													<ul className="unit-reservation-page__pricing_items">
														{facility?.fees?.admin
															?.value > 0 && (
															<li>
																<Text
																	color="black"
																	padding="left-small"
																	size="smaller"
																>
																	One-Time
																	Admin Fee:{' '}
																	{
																		facility
																			?.fees
																			?.admin
																			?.text
																	}
																</Text>
															</li>
														)}
														{facility?.fees?.lockFee
															?.value > 0 && (
															<li>
																<Text
																	color="black"
																	size="smaller"
																>
																	One-Time
																	Lock Fee: $
																	{
																		facility
																			?.fees
																			?.lockFee
																			?.text
																	}
																</Text>
															</li>
														)}
													</ul>
												</div>
											</div>
											<Paragraph
												color="gray"
												weight="standard"
												className='unit-reservation-page__admin-fee-description'
											>
												The one-time, non-refundable
												admin fee is charged upon
												signing your lease agreement.
												This fee covers the costs
												associated with processing your
												application and setting up your
												account and access credentials,
												ensuring a smooth move-in
												process.
											</Paragraph>
										</div>
									)}
								</Card>
							</div>
						</Card>
					</div>
					<div className="unit-reservation-page__right">
						<Card>
							<div className="divider">
								<H2
									size="medium"
									className="checkoutpage-reservation-header"
								>
									Reserve today to lock in this rate now
								</H2>
								<div className="check-with-text-container">
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											Risk free
										</span>
									</div>
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											Cancel anytime
										</span>
									</div>
									<div className="check-with-text">
										<span
											className="check-with-text__icon"
											aria-hidden
										>
											<Image
												src={checkImage}
												alt="check"
											/>
										</span>
										<span className="check-with-text__label">
											No payment required
										</span>
									</div>
								</div>
							</div>
							<UnitReservationFormV2
								facility={facility}
								unit={unit}
								onSubmit={actions.createBooking}
								phone={phone}
							/>
						</Card>
					</div>
				</div>
			</div>
		</Page>
	);
}

UnitReservationPage.propTypes = {
	actions: PropTypes.object,
	unit: PropTypes.object,
	facility: PropTypes.object,
	phone: PropTypes.object,
	creditCards: PropTypes.array,
};

export default enhance(UnitReservationPage);
